import { supabase } from './supabase';
import { StructuredAddress, formatStructuredAddress, postalFsaPrefix } from './storefront';

// ─────────────────────────────────────────────────────────────────────────────
// Customer delivery-address book (staff side).
//
// customer_addresses is the SINGLE SOURCE OF TRUTH for delivery addresses:
// the online checkout and customer portal already read/write it, and the
// staff order flows go through this module. customers.address remains the
// customer's PHYSICAL address (bookings/mobile jobs) and is never touched
// here; customers.default_shipping_address is kept as a write-through copy
// of the default card so legacy readers (checkout prefill) stay consistent.
// ─────────────────────────────────────────────────────────────────────────────

export interface CustomerAddress {
  id: string;
  business_id: string;
  customer_id: string;
  label: string | null;
  company: string | null;
  line1: string;
  line2: string | null;
  city: string | null;
  province: string | null;
  postal_code: string | null;
  country: string | null;
  phone: string | null;
  delivery_instructions: string | null;
  is_default_shipping: boolean;
  is_default_billing: boolean;
}

/** Address-book row → the structured shape checkout/pricing/orders use. */
export function customerAddressToStructured(a: CustomerAddress): StructuredAddress {
  const structured: StructuredAddress = {
    company: a.company || '',
    line1: a.line1 || '',
    line2: a.line2 || '',
    city: a.city || '',
    province: a.province || '',
    postal_code: a.postal_code || '',
    country: a.country || 'Canada',
    formatted: '',
  };
  structured.formatted = formatStructuredAddress(structured);
  return structured;
}

/** One-line display for cards. */
export function customerAddressLabel(a: CustomerAddress): string {
  return [a.line1, a.line2, a.city, [a.province, a.postal_code].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
}

/** Default shipping first, then newest. */
export async function listCustomerAddresses(customerId: string): Promise<CustomerAddress[]> {
  const { data, error } = await supabase
    .from('customer_addresses')
    .select('*')
    .eq('customer_id', customerId)
    .order('is_default_shipping', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as CustomerAddress[];
}

/** Write-through: mirror the default card into customers.default_shipping_address
 *  so legacy readers (checkout prefill for customers with no session) stay in sync. */
async function syncDefaultToCustomer(customerId: string): Promise<void> {
  const { data } = await supabase
    .from('customer_addresses')
    .select('*')
    .eq('customer_id', customerId)
    .eq('is_default_shipping', true)
    .maybeSingle();
  const structured = data ? customerAddressToStructured(data as CustomerAddress) : null;
  await supabase
    .from('customers')
    .update({ default_shipping_address: structured })
    .eq('id', customerId);
}

export interface SaveCustomerAddressInput {
  /** Present = edit that card; absent = create (deduped on street+postal). */
  id?: string;
  address: StructuredAddress;
  deliveryInstructions?: string | null;
  phone?: string | null;
  label?: string | null;
  makeDefaultShipping?: boolean;
}

/**
 * Create or update an address card. Creates dedupe against an existing card
 * with the same street + postal (that card is updated instead, so repeat
 * orders never pile up duplicates). A customer's first card automatically
 * becomes their default shipping address.
 */
export async function saveCustomerAddress(
  businessId: string,
  customerId: string,
  input: SaveCustomerAddressInput
): Promise<CustomerAddress> {
  const a = input.address;
  if (!a.line1.trim()) throw new Error('Street address is required');

  const row = {
    company: a.company?.trim() || null,
    line1: a.line1.trim(),
    line2: a.line2?.trim() || null,
    city: a.city?.trim() || null,
    province: a.province?.trim() || null,
    postal_code: a.postal_code?.trim() || null,
    country: a.country?.trim() || null,
    ...(input.deliveryInstructions !== undefined ? { delivery_instructions: input.deliveryInstructions?.trim() || null } : {}),
    ...(input.phone !== undefined ? { phone: input.phone?.trim() || null } : {}),
    ...(input.label !== undefined ? { label: input.label?.trim() || null } : {}),
  };

  let targetId = input.id ?? null;

  if (!targetId) {
    // Dedupe on street + postal FSA-insensitive match.
    const { data: existing } = await supabase
      .from('customer_addresses')
      .select('id, line1, postal_code')
      .eq('customer_id', customerId);
    const norm = (s: string | null | undefined) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const dupe = (existing || []).find(
      (e) =>
        norm(e.line1) === norm(row.line1) &&
        postalFsaPrefix(e.postal_code || '') === postalFsaPrefix(row.postal_code || '')
    );
    if (dupe) targetId = dupe.id;
  }

  const isFirst = !targetId && !((await supabase
    .from('customer_addresses')
    .select('id')
    .eq('customer_id', customerId)
    .limit(1)
    .maybeSingle()).data);

  const makeDefault = input.makeDefaultShipping || isFirst;

  let saved: CustomerAddress;
  if (targetId) {
    const { data, error } = await supabase
      .from('customer_addresses')
      .update(row)
      .eq('id', targetId)
      .select('*')
      .single();
    if (error) throw error;
    saved = data as CustomerAddress;
  } else {
    const { data, error } = await supabase
      .from('customer_addresses')
      .insert({ business_id: businessId, customer_id: customerId, ...row })
      .select('*')
      .single();
    if (error) throw error;
    saved = data as CustomerAddress;
  }

  if (makeDefault && !saved.is_default_shipping) {
    await setDefaultShippingAddress(customerId, saved.id);
    saved = { ...saved, is_default_shipping: true };
  } else if (saved.is_default_shipping) {
    // Edited the default card — refresh the write-through copy.
    await syncDefaultToCustomer(customerId);
  }

  return saved;
}

export async function setDefaultShippingAddress(customerId: string, addressId: string): Promise<void> {
  // Partial unique index allows only one default — clear first, then set.
  const { error: clearErr } = await supabase
    .from('customer_addresses')
    .update({ is_default_shipping: false })
    .eq('customer_id', customerId)
    .eq('is_default_shipping', true);
  if (clearErr) throw clearErr;
  const { error } = await supabase
    .from('customer_addresses')
    .update({ is_default_shipping: true })
    .eq('id', addressId);
  if (error) throw error;
  await syncDefaultToCustomer(customerId);
}

export async function deleteCustomerAddress(customerId: string, addressId: string): Promise<void> {
  const { error } = await supabase.from('customer_addresses').delete().eq('id', addressId);
  if (error) throw error;
  await syncDefaultToCustomer(customerId);
}
